import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { emptyContractFields, type ContractFields, type DocumentMeta, type Outcome } from "../../shared/contract";
import { relativeDays } from "../../shared/dashboard";
import { currentTermEnd } from "../../shared/contract";
import { diffDays, formatDate } from "../../shared/dates";
import { dateOrderForLocale, suggestFromText, type Suggestions } from "../../shared/suggest";
import { DEFAULT_AI_MODEL, mergeAiSuggestions, type AiSuggestions } from "../../shared/ai";
import { planScan } from "../../shared/ai-credits";
import { aiExtras, coverage, creditsLine, readWithAi, type FullScanOffer, type ScanInfo } from "../lib/ai";
import { draftFromRules, rulesFound } from "../../shared/draft";

import { ContractForm } from "../components/ContractForm";
import { Alert, Badge, Button, Card, CardTitle, Dialog, Field, PageHeader, Spinner, TextInput, buttonClass, cx, focusRing, formatBytes } from "../components/ui";
import { api, ApiError } from "../lib/api";
import { db } from "../lib/db";
import { extractText, type ExtractResult } from "../lib/extract";
import { checkFile, uploadFile } from "../lib/files";
import { formatDateTime, formatDetailValue, groupedDetails, historyLabel, renewalText, suggestedRenewalEnd, typeLine, usualCurrency } from "../lib/display";
import { engine, useContract, useContracts, useOnline, useSession } from "../lib/session";

/** Waits until a locally created contract has reached the server, so files can be attached to it. */
async function waitForServer(id: string, timeoutMs = 15_000): Promise<boolean> {
  const until = Date.now() + timeoutMs;
  while (Date.now() < until) {
    await engine.sync();
    if ((await db.outbox.where("contractId").equals(id).count()) === 0) return Boolean(await db.contracts.get(id));
    if (!navigator.onLine) return false;
    await new Promise((r) => setTimeout(r, 500));
  }
  return false;
}

function FilePicker({ onFile, disabled }: { onFile: (f: File) => void; disabled?: boolean }) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const file = e.dataTransfer.files[0];
        if (file && !disabled) onFile(file);
      }}
      className={cx("rounded-xl border-2 border-dashed px-6 py-8 text-center text-sm transition-colors", over ? "border-accent bg-accent-soft" : "border-line-strong bg-surface")}
    >
      <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-accent-soft text-accent-fg">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden>
          <path d="M12 16V4m0 0-4 4m4-4 4 4M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
        </svg>
      </div>
      <p className="mb-3 text-muted">Drop a PDF or Word file here, or</p>
      <Button variant="secondary" disabled={disabled} onClick={() => input.current?.click()}>
        Choose file
      </Button>
      <input
        ref={input}
        type="file"
        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        className="hidden"
        data-testid="file-input"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = "";
        }}
      />
      <p className="mt-3 text-xs text-faint">Up to 20 MB. The text is read on your device to suggest the parties, value and dates.</p>
    </div>
  );
}

export function NewContract() {
  const { me, today } = useSession();
  const contracts = useContracts();
  const navigate = useNavigate();
  const online = useOnline();
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [extracting, setExtracting] = useState<string | null>(null);
  const [extracted, setExtracted] = useState<ExtractResult | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestions>({});
  const [initial, setInitial] = useState<ContractFields>(emptyContractFields());
  const [formKey, setFormKey] = useState(0);
  const [id] = useState(() => crypto.randomUUID());
  const [aiKeys, setAiKeys] = useState<Set<string>>(new Set());
  const [aiNote, setAiNote] = useState<{ tone: "good" | "warn"; text: string } | null>(null);
  const aiAbort = useRef<AbortController | null>(null);
  const [aiRunning, setAiRunning] = useState(false);
  /** After a partial quick scan: what a full scan would cost. */
  const [fullOffer, setFullOffer] = useState<{ offer: FullScanOffer; scan: ScanInfo; canBuy: boolean } | null>(null);
  const [fullRunning, setFullRunning] = useState(false);
  const [aiPatch, setAiPatch] = useState<{ id: number; suggestions: AiSuggestions } | null>(null);
  const alwaysFull = !!me?.preferences?.aiFullScan;

  /** Lets an open model fill what the rules missed. Never overwrites what's already there. */
  async function fillWithAi(text: string, base: ContractFields) {
    if (!online || text.trim().length < 40) return;
    const ctrl = new AbortController();
    aiAbort.current = ctrl;
    setAiRunning(true);
    setFullOffer(null);
    if (alwaysFull) {
      const plan = planScan(text, "full", DEFAULT_AI_MODEL);
      setExtracting(
        plan.calls > 2
          ? `Full scan is on in Settings, so AI is reading the whole document (about ${Math.ceil(plan.typicalMilli / 1000)} credits). This can take a minute…`
          : "Full scan is on in Settings, so AI is reading the whole document…",
      );
    } else setExtracting("Reading with AI (quick scan)…");
    const result = await readWithAi(text, ctrl.signal, alwaysFull ? "full" : "quick");
    aiAbort.current = null;
    setAiRunning(false);
    if (!result.ok) {
      if (!result.quiet) setAiNote({ tone: "warn", text: result.message });
      return;
    }
    const { fields, aiKeys: keys, filled } = mergeAiSuggestions(base, result.suggestions);
    const credits = `${creditsLine(result.charged, result.credits)} ${aiExtras(result)}`.trim();
    setInitial(fields);
    setAiKeys(keys);
    setFormKey((k) => k + 1);
    if (result.scan?.partial && result.fullScan) setFullOffer({ offer: result.fullScan, scan: result.scan, canBuy: !!result.credits?.topUp.available });
    const how = result.scan?.mode === "full" && result.scan.totalChars > 20_000 ? "AI read the whole document (full scan). " : "";
    setAiNote(
      filled
        ? { tone: "good", text: `${how}AI filled in ${filled} more detail${filled === 1 ? "" : "s"}. Check them before saving: AI can be wrong. ${credits}`.trim() }
        : { tone: "good", text: `${how}AI didn't find anything more to add. ${credits}`.trim() },
    );
  }

  /** Reads the whole document after a quick scan and adds what it finds to the form as it stands. */
  async function runFullScan() {
    if (!extracted || fullRunning) return;
    setFullRunning(true);
    const result = await readWithAi(extracted.text, undefined, "full");
    setFullRunning(false);
    if (!result.ok) {
      if (!result.quiet) setAiNote({ tone: "warn", text: result.message });
      return;
    }
    setFullOffer(null);
    setAiPatch({ id: Date.now(), suggestions: result.suggestions });
    setAiNote({ tone: "good", text: `${result.downgraded ? "" : "Full scan done. "}${creditsLine(result.charged, result.credits)} ${aiExtras(result)}`.trim() });
  }

  async function pick(f: File) {
    setFileError(null);
    const problem = checkFile(f, me?.limits.maxFileBytes);
    if (problem) return setFileError(problem);
    setFile(f);
    setExtracting("Reading the document…");
    try {
      const result = await extractText(f, setExtracting);
      setExtracted(result);
      const s = suggestFromText(result.text, dateOrderForLocale(navigator.language), {
        ownNames: [me?.org.name, me?.user.name].filter((n): n is string => !!n),
      });
      setSuggestions(s);
      const fromRules = draftFromRules(initial, s, f.name);
      setInitial(fromRules);
      setFormKey((k) => k + 1);
      if (result.note) setFileError(result.note);
      await fillWithAi(result.text, fromRules);
    } catch (err) {
      setExtracted(null);
      setFileError(`${err instanceof Error ? err.message : "Couldn't read the file."} You can still fill in the details yourself.`);
      setInitial((prev) => ({ ...prev, title: prev.title || f.name.replace(/\.(pdf|docx)$/i, "") }));
      setFormKey((k) => k + 1);
    } finally {
      setExtracting(null);
    }
  }

  async function save(fields: ContractFields) {
    await engine.createContract(id, { ...fields, ownerUserId: fields.ownerUserId ?? me?.user.id ?? null }, me?.user.id ?? null);
    if (file) {
      if (!(await waitForServer(id))) {
        navigate(`/contracts/${id}`, { state: { notice: "Saved on this device. Attach the file again once you're online." } });
        return;
      }
      try {
        await uploadFile(id, file, extracted);
        await engine.sync();
      } catch (err) {
        navigate(`/contracts/${id}`, { state: { notice: `The contract was saved, but the file didn't upload: ${err instanceof Error ? err.message : "unknown error"}` } });
        return;
      }
    }
    navigate(`/contracts/${id}`);
  }

  const found = rulesFound(suggestions);
  // Wait for the local cache (a few ms) so the form starts with the workspace's usual currency.
  if (!contracts) return <Spinner />;
  return (
    <div className="space-y-6">
      <PageHeader title="Add a contract" description="Upload the document to fill in the details automatically, or type them in." />
      {!file && <FilePicker onFile={pick} disabled={!online} />}
      {!online && !file && <Alert tone="warn">You're offline. You can save the details now and attach the file later.</Alert>}
      {file && (
        <Card className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="truncate font-medium">{file.name}</div>
            <div className="text-xs text-faint">
              {extracting ?? (extracted ? (found ? `We found ${found} detail${found === 1 ? "" : "s"}. Check them below.` : "We couldn't find any details. Fill them in below.") : "")}
            </div>
          </div>
          <Button variant="ghost" onClick={() => { setFile(null); setExtracted(null); setSuggestions({}); setAiNote(null); setFullOffer(null); }} disabled={!!extracting || fullRunning}>
            Remove
          </Button>
        </Card>
      )}
      {fileError && <Alert tone="warn">{fileError}</Alert>}
      {aiNote && !extracting && <Alert tone={aiNote.tone === "good" ? "info" : "warn"}>{aiNote.text}</Alert>}
      {fullOffer && !extracting && (
        <Card className="space-y-2 border-accent/30">
          <p className="text-sm">
            <strong className="font-semibold">This is a big document, so AI ran a quick scan</strong> of the first pages and the most relevant parts ({coverage(fullOffer.scan)}).
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="secondary" size="sm" onClick={() => void runFullScan()} disabled={fullRunning || !fullOffer.offer.affordable}>
              {fullRunning ? "Reading the whole document…" : `Complete the scan · about ${fullOffer.offer.estimate} credits`}
            </Button>
            <Link to="/settings#ai-credits" className="text-xs font-medium text-muted underline underline-offset-2 hover:text-fg">
              Always run a full scan
            </Link>
          </div>
          {!fullOffer.offer.affordable && (
            <p className="text-xs text-warn-fg">
              A full scan needs up to {fullOffer.offer.needs} credits, more than you have left this month.{" "}
              {fullOffer.canBuy ? (
                <Link to="/settings#buy-credits" className="font-medium underline underline-offset-2">
                  Buy credits
                </Link>
              ) : (
                "Buying more credits is coming soon."
              )}
            </p>
          )}
          {fullRunning && <p className="text-xs text-faint">This can take a minute. You can keep editing the form; nothing you've typed will be replaced.</p>}
        </Card>
      )}
      {extracting ? (
        <div className="space-y-3 text-center">
          <Spinner label={extracting} />
          {aiRunning && (
            <>
              <p className="text-xs text-faint">The text is read by an open AI model on ClauseKeep's servers. It isn't used for training.</p>
              <Button variant="secondary" size="sm" onClick={() => aiAbort.current?.abort()}>
                Skip AI
              </Button>
            </>
          )}
        </div>
      ) : (
        <Card>
          <ContractForm
            key={formKey}
            initial={initial}
            suggestedType={suggestions.type?.value}
            aiKeys={aiKeys}
            aiPatch={aiPatch}
            onAiPatched={(added, keys) => {
              setAiKeys((prev) => new Set([...prev, ...keys]));
              setAiNote((n) => (n ? { ...n, text: `${n.text} It added ${added} more detail${added === 1 ? "" : "s"}.` } : n));
            }}
            defaultCurrency={usualCurrency(contracts)}
            members={me?.members ?? []}
            currentUserId={me?.user.id}
            suggestions={suggestions}
            today={today}
            submitLabel="Save contract"
            onSubmit={save}
            onCancel={() => navigate(-1)}
          />
        </Card>
      )}
    </div>
  );
}

function Documents({ contractId, documents }: { contractId: string; documents: DocumentMeta[] }) {
  const online = useOnline();
  const { me } = useSession();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<DocumentMeta | null>(null);

  async function add(file: File) {
    setError(null);
    const problem = checkFile(file, me?.limits.maxFileBytes);
    if (problem) return setError(problem);
    setBusy("Reading the document…");
    let extracted: ExtractResult | null = null;
    try {
      extracted = await extractText(file, setBusy);
    } catch {
      extracted = null;
    }
    try {
      await uploadFile(contractId, file, extracted, (f) => setBusy(`Uploading… ${Math.round(f * 100)}%`));
      await engine.sync();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed.");
    } finally {
      setBusy(null);
    }
  }

  async function remove(doc: DocumentMeta) {
    setConfirmDelete(null);
    setBusy("Deleting…");
    try {
      await api(`/api/documents/${doc.id}`, { method: "DELETE" });
      await engine.sync();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't delete the file.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card className="space-y-3">
      <CardTitle>Files</CardTitle>
      {documents.length === 0 && <p className="text-sm text-faint">No files attached.</p>}
      <ul className="divide-y divide-line">
        {documents.map((d) => (
          <li key={d.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
            <span className="min-w-0 flex-1 truncate">{d.filename}</span>
            <span className="text-xs tabular-nums text-faint">{formatBytes(d.sizeBytes)}</span>
            {d.contentType === "application/pdf" && (
              <a className="font-medium text-accent-fg hover:underline" href={`/api/documents/${d.id}/file?inline=1`} target="_blank" rel="noopener">
                View
              </a>
            )}
            <a className="font-medium text-accent-fg hover:underline" href={`/api/documents/${d.id}/file`} download={d.filename}>
              Download
            </a>
            <button type="button" className="font-medium text-bad hover:underline disabled:opacity-50" disabled={!online || !!busy} onClick={() => setConfirmDelete(d)}>
              Delete
            </button>
          </li>
        ))}
      </ul>
      {busy ? <Spinner label={busy} /> : online ? <FilePicker onFile={add} /> : <p className="text-sm text-faint">Go online to add files.</p>}
      {error && <Alert tone="bad">{error}</Alert>}
      <Dialog open={!!confirmDelete} title="Delete this file?" onClose={() => setConfirmDelete(null)}>
        <p className="text-sm">“{confirmDelete?.filename}” will be permanently deleted. The contract stays.</p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setConfirmDelete(null)}>
            Keep it
          </Button>
          <Button variant="danger" onClick={() => confirmDelete && void remove(confirmDelete)}>
            Delete file
          </Button>
        </div>
      </Dialog>
    </Card>
  );
}

function OutcomeDialog({
  outcome,
  onClose,
  onConfirm,
  needsDate,
  suggestedEnd,
}: {
  outcome: Outcome | null;
  onClose: () => void;
  onConfirm: (d: string | null) => Promise<void>;
  needsDate: boolean;
  /** A year after the current end, offered when a renewal needs a new end date. */
  suggestedEnd: string | null;
}) {
  const [date, setDate] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setDate(outcome === "renewed" && needsDate && suggestedEnd ? suggestedEnd : "");
    setError(null);
  }, [outcome, needsDate, suggestedEnd]);
  if (!outcome) return null;
  const labels: Record<Outcome, string> = { renewed: "Mark as renewed", renegotiated: "Mark as renegotiated", terminated: "Mark as terminated" };
  return (
    <Dialog open title={labels[outcome]} onClose={onClose}>
      {outcome === "terminated" ? (
        <p className="text-sm">We'll stop reminding you about this contract. You can still find it under Terminated.</p>
      ) : (
        <Field label={needsDate && outcome === "renewed" ? "New end date" : "New end date (optional)"} hint={outcome === "renewed" ? (needsDate ? "Suggested: a year after the current end. Change it to match the renewal." : "Leave empty to extend by the renewal term.") : undefined} error={error ?? undefined}>
          {(id) => <TextInput id={id} type="date" value={date} onChange={(e) => setDate(e.target.value)} />}
        </Field>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant={outcome === "terminated" ? "danger" : "primary"}
          disabled={busy}
          onClick={async () => {
            if (outcome === "renewed" && needsDate && !date) return setError("Enter the new end date.");
            setBusy(true);
            try {
              await onConfirm(date || null);
              onClose();
            } catch (err) {
              setError(err instanceof Error ? err.message : "Couldn't save.");
            } finally {
              setBusy(false);
            }
          }}
        >
          Confirm
        </Button>
      </div>
    </Dialog>
  );
}

export function ContractView() {
  const { id } = useParams();
  const contract = useContract(id);
  const { today, me } = useSession();
  const navigate = useNavigate();
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [history, setHistory] = useState<{ id: number; type: string; createdAt: string; actorEmail: string | null }[] | null>(null);
  const notice = (history === null ? (window.history.state?.usr?.notice as string | undefined) : undefined) ?? null;

  useEffect(() => {
    if (!id || !contract || contract.pending) return;
    api<{ events: typeof history }>(`/api/contracts/${id}/history`)
      .then((r) => setHistory(r.events))
      .catch(() => setHistory([]));
  }, [id, contract?.version, contract?.pending, contract]);

  if (contract === undefined) return <Spinner />;
  if (contract === null || contract.deletedAt) {
    return (
      <Card className="space-y-3 text-center">
        <h1 className="text-lg font-semibold tracking-tight">Contract not found</h1>
        <p className="text-sm text-muted">It may have been deleted, or the link is wrong.</p>
        <Link className="font-medium text-accent-fg hover:underline" to="/contracts">
          Back to contracts
        </Link>
      </Card>
    );
  }

  const termEnd = currentTermEnd(contract, today);
  const deadline = contract.nextNoticeDeadline;
  const daysLeft = deadline ? diffDays(today, deadline) : null;
  const owner = me?.members.find((m) => m.id === contract.ownerUserId);

  return (
    <div className="space-y-4">
      {notice && <Alert tone="warn">{notice}</Alert>}
      <Link to="/contracts" className={cx("inline-flex items-center gap-1 rounded text-sm font-medium text-muted hover:text-fg", focusRing)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden>
          <path d="m15 18-6-6 6-6" />
        </svg>
        Contracts
      </Link>
      <PageHeader
        title={contract.title}
        description={typeLine(contract.type) || undefined}
        actions={
          <>
            <Link to={`/contracts/${contract.id}/edit`} className={buttonClass("secondary")}>
              Edit
            </Link>
            <Button variant="ghost" className="text-bad hover:bg-bad-bg hover:text-bad" onClick={() => setConfirmDelete(true)}>
              Delete
            </Button>
          </>
        }
      />
      {contract.pending && <Alert tone="info">Saved on this device. It will sync when you're online.</Alert>}

      {contract.status === "active" && deadline && (
        <Card highlight={daysLeft !== null && daysLeft <= 30}>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1">
              <div className="text-sm font-medium text-muted">Notice deadline</div>
              <div className="text-xl font-semibold tracking-tight tabular-nums">
                {formatDate(deadline)} <span className="text-base font-medium text-muted">({daysLeft !== null && daysLeft < 0 ? "passed" : relativeDays(daysLeft)})</span>
              </div>
              <div className="mt-0.5 text-sm text-muted">
                {contract.renewalType === "auto" ? `Renews automatically on ${formatDate(termEnd)} unless you give notice.` : `Ends on ${formatDate(termEnd)}.`}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" onClick={() => setOutcome("renewed")}>Renewed</Button>
              <Button variant="secondary" onClick={() => setOutcome("renegotiated")}>Renegotiated</Button>
              <Button variant="secondary" onClick={() => setOutcome("terminated")}>Terminated</Button>
            </div>
          </div>
        </Card>
      )}
      {contract.status === "terminated" && <Alert tone="info">This contract is marked terminated. No reminders are sent.</Alert>}

      <Card>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-5 text-sm sm:grid-cols-3">
          {[
            ["Start", formatDate(contract.startDate)],
            ["End (current term)", formatDate(termEnd)],
            ["Renewal", renewalText(contract)],
            ["Notice period", contract.noticePeriodDays === null ? "—" : `${contract.noticePeriodDays} days`],
            ["Owner", owner ? (owner.name ?? owner.email) : "—"],
          ].map(([k, v]) => (
            <div key={k}>
              <dt className="text-xs font-medium tracking-wide text-faint uppercase">{k}</dt>
              <dd className="mt-1 font-medium text-fg">{v}</dd>
            </div>
          ))}
        </dl>
        {contract.tags.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-1.5 border-t border-line pt-4">
            {contract.tags.map((t) => (
              <Badge key={t}>#{t}</Badge>
            ))}
          </div>
        )}
        {contract.notes && <p className="mt-4 whitespace-pre-wrap border-t border-line pt-4 text-sm leading-relaxed text-muted">{contract.notes}</p>}
      </Card>

      {contract.parties?.length > 0 && (
        <Card>
          <CardTitle>People and companies</CardTitle>
          <ul className="mt-3 grid gap-3 sm:grid-cols-2">
            {contract.parties.map((p, i) => (
              <li key={i} className="rounded-lg border border-line px-3 py-2.5 text-sm">
                <div className="text-xs font-medium tracking-wide text-faint uppercase">{p.role || "Party"}</div>
                <div className="mt-0.5 font-medium text-fg">{p.name}</div>
                {(p.email || p.phone) && <div className="mt-0.5 text-xs text-muted">{[p.email, p.phone].filter(Boolean).join(" · ")}</div>}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {Object.keys(contract.details ?? {}).length > 0 && (
        <Card className="space-y-5">
          <CardTitle>Key terms</CardTitle>
          {groupedDetails(contract.details).map((g) => (
            <section key={g.group} aria-label={g.label}>
              <h3 className="mb-2 text-xs font-semibold tracking-wide text-faint uppercase">{g.label}</h3>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-3 text-sm sm:grid-cols-2">
                {g.items.map((d) => (
                  <div key={d.key}>
                    <dt className="text-muted">{d.label}</dt>
                    <dd className="mt-0.5 font-medium whitespace-pre-wrap text-fg">{formatDetailValue(d.key, d.value, contract.currency)}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </Card>
      )}

      {!contract.pending && <Documents contractId={contract.id} documents={contract.documents} />}

      {history && history.length > 0 && (
        <Card>
          <h2 className="mb-3 text-base font-semibold tracking-tight">History</h2>
          <ul className="space-y-1.5 text-sm text-muted">
            {history.map((e) => (
              <li key={e.id} className="flex flex-wrap gap-x-2">
                <span className="font-medium text-fg">{historyLabel(e.type)}</span>
                <span>
                  {formatDateTime(e.createdAt)}
                  {e.actorEmail ? ` · ${e.actorEmail}` : ""}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <OutcomeDialog
        outcome={outcome}
        needsDate={!contract.renewalTermMonths}
        suggestedEnd={suggestedRenewalEnd(termEnd)}
        onClose={() => setOutcome(null)}
        onConfirm={(d) => engine.recordOutcome(contract.id, outcome!, d)}
      />
      <Dialog open={confirmDelete} title="Delete this contract?" onClose={() => setConfirmDelete(false)}>
        <p className="text-sm">
          “{contract.title}” and its {contract.documents.length} file{contract.documents.length === 1 ? "" : "s"} will be permanently deleted, and you'll get no more reminders for it. This can't be undone.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
            Keep it
          </Button>
          <Button
            variant="danger"
            onClick={async () => {
              await engine.deleteContract(contract.id);
              navigate("/contracts", { replace: true });
            }}
          >
            Delete contract
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

export function EditContract() {
  const { id } = useParams();
  const contract = useContract(id);
  const { me, today } = useSession();
  const navigate = useNavigate();
  if (contract === undefined) return <Spinner />;
  if (!contract || contract.deletedAt) return <Alert tone="bad">That contract doesn't exist anymore.</Alert>;
  return (
    <div className="space-y-4">
      <PageHeader title="Edit contract" />
      <Card>
        <ContractForm
          initial={contract}
          members={me?.members ?? []}
          currentUserId={me?.user.id}
          today={today}
          submitLabel="Save changes"
          onSubmit={async (fields) => {
            try {
              await engine.updateContract(contract.id, fields);
            } catch (err) {
              throw err instanceof ApiError ? err : new Error(err instanceof Error ? err.message : "Couldn't save.");
            }
            navigate(`/contracts/${contract.id}`);
          }}
          onCancel={() => navigate(-1)}
        />
      </Card>
    </div>
  );
}
